"use client"

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import type { LinkType } from "@/lib/referral-types"

export const LINK_TYPES_KEY = ["link-types"] as const

export const useLinkTypes = () => {
  return useQuery<LinkType[]>({
    queryKey: LINK_TYPES_KEY,
    queryFn: async () => {
      const res = await fetch("/api/referral/link-types")
      if (!res.ok) throw new Error("Failed to fetch link types")
      return res.json()
    },
    staleTime: 60_000,
  })
}

export const useCreateLinkType = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      namespace: string
      label: string
      description?: string
    }) => {
      const res = await fetch("/api/referral/link-types", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error ?? "Failed to create link type")
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LINK_TYPES_KEY })
    },
  })
}

export const useUpdateLinkType = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      namespace: string
      label?: string
      description?: string
      is_active?: boolean
    }) => {
      const res = await fetch("/api/referral/link-types", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error ?? "Failed to update link type")
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LINK_TYPES_KEY })
    },
  })
}
